/**
 * Groene-kaart-extractie (voertuig-upload) — gedeeld response-contract.
 *
 * De voertuig-upload met documenttype GROENE_KAART toont een blok
 * "Voertuigvelden bijwerken": de API leest de kaart één keer uit via de
 * AI (native PDF + vision) en stelt de verzekeringsvelden van het voertuig
 * voor. Dit bestand is het gedeelde contract tussen de API-route
 * (`POST /voertuigen/:id/groene-kaart-extractie`) en het upload-formulier.
 *
 * Principes:
 *  - Fail-soft: AI gepauzeerd/tegoed op/onleesbaar bestand => 200 met lege
 *    velden + `aiBeschikbaar: false` (+ reden). Het blok werkt dan handmatig;
 *    een AI-storing blokkeert nooit een upload.
 *  - Nooit stil schrijven: dit zijn VOORSTELLEN. De daadwerkelijke write loopt
 *    pas na bevestiging via de bestaande `PATCH /voertuigen/:id`.
 */

import { z } from 'zod'

// Kalender-geldig, niet alleen het patroon: "2026-02-31" rolt bij new Date()
// stil door naar 3 maart - de round-trip-refine weigert zulke waarden.
const isoDatum = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((t) => {
    const d = new Date(`${t}T00:00:00.000Z`)
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === t
  }, 'Geen geldige kalenderdatum')

/** De vijf voertuigvelden die de groene kaart kan voeden (spiegel van `updateVoertuigSchema`). */
export const GroeneKaartVeldenSchema = z.object({
  groeneKaartNummer: z.string().max(500).nullable(),
  groeneKaartGeldigVanaf: isoDatum.nullable(),
  groeneKaartGeldigTot: isoDatum.nullable(),
  verzekeraar: z.string().max(500).nullable(),
  verzekeringPolisNummer: z.string().max(500).nullable(),
})
export type GroeneKaartVelden = z.infer<typeof GroeneKaartVeldenSchema>

export const GroeneKaartExtractieDTOSchema = z.object({
  velden: GroeneKaartVeldenSchema,
  /** Kenteken zoals van de kaart gelezen (genormaliseerd), of null. */
  kenteken: z.string().nullable(),
  /** false = de kaart vermeldt een ANDER kenteken dan dit voertuig; null = geen kenteken gelezen. */
  kentekenMatch: z.boolean().nullable(),
  /** false = er is geen AI-voorstel (pauze/tegoed/onleesbaar); velden zijn dan leeg. */
  aiBeschikbaar: z.boolean(),
  /** Mensleesbare toelichting wanneer `aiBeschikbaar` false is (of null). */
  reden: z.string().nullable(),
})
export type GroeneKaartExtractieDTO = z.infer<typeof GroeneKaartExtractieDTOSchema>

/** Lege velden-set (handmatige invoer / AI niet beschikbaar). */
export const LEGE_GROENE_KAART_VELDEN: GroeneKaartVelden = {
  groeneKaartNummer: null,
  groeneKaartGeldigVanaf: null,
  groeneKaartGeldigTot: null,
  verzekeraar: null,
  verzekeringPolisNummer: null,
}
